import React, { forwardRef, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import MaskedInput from "react-text-mask";
import { benefitsOptions } from "../data/benefitsOptions";
import HealthPlanOperatorFieldset from "./HealthPlanOperatorFieldset";
import DentalPlanOperatorFieldset from "./DentalPlanOperatorFieldset";
import AgreementFieldset from "./AgreementFieldset";

const ClosePeriodMask = forwardRef(function ClosePeriodMask(props, ref) {
  return (
    <MaskedInput
      {...props}
      ref={ref.inputElement}
      mask={[/\d/, /\d/, "/", /\d/, /\d/]}
      guide={false}
      keepCharPositions={false}
      placeholderChar={" "}
      showMask
    />
  );
});

const sectionTitleSx = { color: "#7A7A7A", fontWeight: "600", fontSize: "14px", my: 0.5 };
const paperSx = { p: { xs: 1.5, sm: 2 }, mx: { xs: 0, sm: 1, md: 2 }, mb: 2 };

export default function Benefits() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const benefitsIsValidated = watch("benefitsIsValidated");
  const hasLifeInsurance = watch("hasLifeInsurance");
  const lifeInsuranceMovementReport = watch("lifeInsuranceMovementReport");
  const lifeInsuranceDiscountValue = watch("lifeInsuranceDiscountValue");
  const lifeInsuranceDiscountOnPayroll = watch("lifeInsuranceDiscountOnPayroll");
  const lifeInsuranceOtherRules = watch("lifeInsuranceOtherRules");
  const hasHealthPlan = watch("hasHealthPlan");
  const healthPlanDiscountOnPayroll = watch("healthPlanDiscountOnPayroll");
  const healthPlanOtherRules = watch("healthPlanOtherRules");
  const hasDentalPlan = watch("hasDentalPlan");
  const dentalPlanDiscountOnPayroll = watch("dentalPlanDiscountOnPayroll");
  const dentalPlanOtherRules = watch("dentalPlanOtherRules");
  const hasTransportationVoucher = watch("hasTransportationVoucher");
  const transportationBenefitForAccidentLeave = watch("transportationBenefitForAccidentLeave");
  const transportationBenefitForIllnessLeave = watch("transportationBenefitForIllnessLeave");
  const transportationBenefitForMaternityLeave = watch("transportationBenefitForMaternityLeave");
  const transportationOtherDeductionFactors = watch("transportationOtherDeductionFactors");
  const transportationPayrollDeduction = watch("transportationPayrollDeduction");
  const hasMobilityVoucher = watch("hasMobilityVoucher");
  const mobilityBenefitForAccidentLeave = watch("mobilityBenefitForAccidentLeave");
  const mobilityBenefitForIllnessLeave = watch("mobilityBenefitForIllnessLeave");
  const mobilityBenefitForMaternityLeave = watch("mobilityBenefitForMaternityLeave");
  const mobilityOtherDeductionFactors = watch("mobilityOtherDeductionFactors");
  const mobilityPayrollDeduction = watch("mobilityPayrollDeduction");
  const hasFoodVoucher = watch("hasFoodVoucher");
  const foodBenefitForAccidentLeave = watch("foodBenefitForAccidentLeave");
  const foodBenefitForIllnessLeave = watch("foodBenefitForIllnessLeave");
  const foodBenefitForMaternityLeave = watch("foodBenefitForMaternityLeave");
  const foodOtherDeductionFactors = watch("foodOtherDeductionFactors");
  const foodPayrollDeduction = watch("foodPayrollDeduction");
  const hasMealVoucher = watch("hasMealVoucher");
  const mealBenefitForAccidentLeave = watch("mealBenefitForAccidentLeave");
  const mealBenefitForIllnessLeave = watch("mealBenefitForIllnessLeave");
  const mealBenefitForMaternityLeave = watch("mealBenefitForMaternityLeave");
  const mealOtherDeductionFactors = watch("mealOtherDeductionFactors");
  const mealPayrollDeduction = watch("mealPayrollDeduction");
  const hasBasicBasket = watch("hasBasicBasket");
  const basicBasketPayrollDeduction = watch("basicBasketPayrollDeduction");
  const hasCanteen = watch("hasCanteen");
  const canteenPayrollDeduction = watch("canteenPayrollDeduction");
  const hasOtherBenefits = watch("hasOtherBenefits");
  const hasPharmacy = watch("hasPharmacy");
  const hasSocialSecurity = watch("hasSocialSecurity");
  const hasPowerOfAttorney = watch("hasPowerOfAttorney");
  const lifeInsuranceEmployeeCost = watch("lifeInsuranceEmployeeCost");

  const { fields: healthPlanFields, append: appendHealthPlan, remove: removeHealthPlan } = useFieldArray({
    control,
    name: "healthPlanOperators",
  });

  const { fields: dentalPlanFields, append: appendDentalPlan, remove: removeDentalPlan } = useFieldArray({
    control,
    name: "dentalPlanOperators",
  });

  const { fields: otherBenefitsFields, append: appendOtherBenefit, remove: removeOtherBenefit } = useFieldArray({
    control,
    name: "otherBenefits",
  });

  const { fields: agreementsFields, append: appendAgreement, remove: removeAgreement } = useFieldArray({
    control,
    name: "agreements",
  });

  const emptyHealthPlanOperator = {
    name: "", cnpj: "", ansCode: "", type: "", companyCost: "", employeeCost: "",
    dependentCost: "", aggregateCost: "", hasCoparticipation: "",
    companyCoparticipationCost: "", employeeCoparticipationCost: "",
    dependentCoparticipationCost: "", aggregateCoparticipationCost: "",
    coparticipationRule: "", coparticipationRuleDescription: "",
    beneficiariesOutsideEmployees: "", beneficiariesOutsideEmployeesDescription: "",
    reportReceptionDate: "", adjustmentDate: "",
  };

  const emptyDentalPlanOperator = {
    cnpj: "", ansCode: "", type: "", companyCost: "", employeeCost: "",
    dependentCost: "", aggregateCost: "",
    beneficiariesOutsideEmployees: "", beneficiariesOutsideEmployeesDescription: "",
    reportReceptionDate: "", adjustmentDate: "",
  };

  const emptyOtherBenefit = { description: "", businessRule: "" };

  const emptyAgreement = {
    description: "", calculationPeriod: "", infoFormat: "", reportDeadline: "",
    payrollDeduction: "", payrollDeductionOther: "", terminationDeduction: "", unusedBenefitDeduction: "",
  };

  useEffect(() => {
    const employeeVal = parseFloat(lifeInsuranceEmployeeCost);
    if (!isNaN(employeeVal) && employeeVal >= 0 && employeeVal <= 100) {
      setValue("lifeInsuranceCompanyCost", String(100 - employeeVal));
    } else {
      setValue("lifeInsuranceCompanyCost", "");
    }
  }, [lifeInsuranceEmployeeCost, setValue]);

  return (
    <>
      {/* Seguro de Vida */}
      <Paper elevation={2} sx={paperSx}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" sx={sectionTitleSx}>Seguro de Vida</Typography>
          <Box>
            <Controller
              name="benefitsIsValidated"
              control={control}
              render={({ field }) => (
                <FormControl required error={!!errors.benefitsIsValidated}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      size="small"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                    <Typography variant="body2">Validado</Typography>
                  </Box>
                  <FormHelperText>{errors.benefitsIsValidated?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Box>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasLifeInsurance"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasLifeInsurance}>
                  <InputLabel id="has-life-insurance-label">Seguro de Vida</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-life-insurance-label"
                    label="Seguro de Vida"
                  >
                    {benefitsOptions.hasLifeInsurance.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasLifeInsurance?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasLifeInsurance === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="lifeInsuranceMovementType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceMovementType}>
                      <InputLabel id="life-insurance-movement-type-label">Movimentação executada</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-movement-type-label"
                        label="Movimentação executada"
                      >
                        {benefitsOptions.lifeInsuranceMovementType.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceMovementType?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={lifeInsuranceMovementReport === "1" ? 6 : 12}>
                    <Controller
                      name="lifeInsuranceMovementReport"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceMovementReport}>
                          <InputLabel id="life-insurance-movement-report-label">Envia relatório de movimentação?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="life-insurance-movement-report-label"
                            label="Envia relatório de movimentação?"
                          >
                            {benefitsOptions.lifeInsuranceMovementReport.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.lifeInsuranceMovementReport?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {lifeInsuranceMovementReport === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceMovementReportDeadline"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo de envio do relatório de movimentação (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.lifeInsuranceMovementReportDeadline}
                            helperText={errors.lifeInsuranceMovementReportDeadline?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "2", "3"].includes(lifeInsuranceDiscountValue) ? 6 : 12}>
                    <Controller
                      name="lifeInsuranceDiscountValue"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceDiscountValue}>
                          <InputLabel id="life-insurance-discount-value-label">Valor desconto</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="life-insurance-discount-value-label"
                            label="Valor desconto"
                          >
                            {benefitsOptions.lifeInsuranceDiscountValue.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.lifeInsuranceDiscountValue?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {lifeInsuranceDiscountValue === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceDiscountValueFix"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Valor Fixo"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: "0.01" }}
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9.,]/g, "");
                              field.onChange(val);
                            }}
                            error={!!errors.lifeInsuranceDiscountValueFix}
                            helperText={errors.lifeInsuranceDiscountValueFix?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {lifeInsuranceDiscountValue === "2" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceDiscountValueSalaryPercentage"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Porcentagem do Salário"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 100, step: "0.01" }}
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9.,]/g, "");
                              field.onChange(val);
                            }}
                            error={!!errors.lifeInsuranceDiscountValueSalaryPercentage}
                            helperText={errors.lifeInsuranceDiscountValueSalaryPercentage?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {lifeInsuranceDiscountValue === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceDiscountValueOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.lifeInsuranceDiscountValueOther}
                            helperText={errors.lifeInsuranceDiscountValueOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceEmployeeCost"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Custo Colaborador (%)"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, max: 100 }}
                      error={!!errors.lifeInsuranceEmployeeCost}
                      helperText={errors.lifeInsuranceEmployeeCost?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceCompanyCost"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Custo Empresa (%)"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, max: 100, readOnly: true }}
                      error={!!errors.lifeInsuranceCompanyCost}
                      helperText={errors.lifeInsuranceCompanyCost?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceProLabore"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceProLabore}>
                      <InputLabel id="life-insurance-pro-labore-label">Pró-labore recebe?</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-pro-labore-label"
                        label="Pró-labore recebe?"
                      >
                        {benefitsOptions.lifeInsuranceProLabore.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceProLabore?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceIntern"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceIntern}>
                      <InputLabel id="life-insurance-intern-label">Estagiário recebe?</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-intern-label"
                        label="Estagiário recebe?"
                      >
                        {benefitsOptions.lifeInsuranceIntern.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceIntern?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceApprentice"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceApprentice}>
                      <InputLabel id="life-insurance-apprentice-label">Aprendiz recebe?</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-apprentice-label"
                        label="Aprendiz recebe?"
                      >
                        {benefitsOptions.lifeInsuranceApprentice.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceApprentice?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceOnLeave"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceOnLeave}>
                      <InputLabel id="life-insurance-on-leave-label">Afastado recebe?</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-on-leave-label"
                        label="Afastado recebe?"
                      >
                        {benefitsOptions.lifeInsuranceOnLeave.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceOnLeave?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={lifeInsuranceDiscountOnPayroll === "5" ? 6 : 12}>
                    <Controller
                      name="lifeInsuranceDiscountOnPayroll"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceDiscountOnPayroll}>
                          <InputLabel id="life-insurance-discount-on-payroll-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="life-insurance-discount-on-payroll-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.lifeInsuranceDiscountOnPayroll.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.lifeInsuranceDiscountOnPayroll?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {lifeInsuranceDiscountOnPayroll === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceDiscountOnPayrollOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.lifeInsuranceDiscountOnPayrollOther}
                            helperText={errors.lifeInsuranceDiscountOnPayrollOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="lifeInsuranceDiscountInTermination"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceDiscountInTermination}>
                      <InputLabel id="life-insurance-discount-in-termination-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="life-insurance-discount-in-termination-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.lifeInsuranceDiscountInTermination.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.lifeInsuranceDiscountInTermination?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={lifeInsuranceOtherRules === "1" ? 6 : 12}>
                    <Controller
                      name="lifeInsuranceOtherRules"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.lifeInsuranceOtherRules}>
                          <InputLabel id="life-insurance-other-rules-label">Outras regras?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="life-insurance-other-rules-label"
                            label="Outras regras?"
                          >
                            {benefitsOptions.lifeInsuranceOtherRules.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.lifeInsuranceOtherRules?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {lifeInsuranceOtherRules === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="lifeInsuranceOtherRulesDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.lifeInsuranceOtherRulesDescription}
                            helperText={errors.lifeInsuranceOtherRulesDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Plano de Saúde */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Plano de Saúde</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasHealthPlan"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasHealthPlan}>
                  <InputLabel id="has-health-plan-label">Plano de Saúde</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-health-plan-label"
                    label="Plano de Saúde"
                  >
                    {benefitsOptions.hasHealthPlan.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasHealthPlan?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasHealthPlan === "1" && (
            <>
              {healthPlanFields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <HealthPlanOperatorFieldset
                    index={index}
                    disabled={benefitsIsValidated}
                    onRemove={() => removeHealthPlan(index)}
                    canRemove={healthPlanFields.length > 1}
                  />
                </Grid>
              ))}
              {!benefitsIsValidated && (
                <Grid item xs={12}>
                  <Button variant="outlined" size="small" onClick={() => appendHealthPlan(emptyHealthPlanOperator)}>
                    Adicionar outra operadora
                  </Button>
                </Grid>
              )}
            </>
          )}

          {hasHealthPlan === "1" && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={healthPlanDiscountOnPayroll === "5" ? 6 : 12}>
                    <Controller
                      name="healthPlanDiscountOnPayroll"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.healthPlanDiscountOnPayroll}>
                          <InputLabel id="health-plan-discount-on-payroll-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="health-plan-discount-on-payroll-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.healthPlanDiscountOnPayroll.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.healthPlanDiscountOnPayroll?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {healthPlanDiscountOnPayroll === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="healthPlanDiscountOnPayrollOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.healthPlanDiscountOnPayrollOther}
                            helperText={errors.healthPlanDiscountOnPayrollOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="healthPlanDiscountInTermination"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.healthPlanDiscountInTermination}>
                      <InputLabel id="health-plan-discount-in-termination-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="health-plan-discount-in-termination-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.healthPlanDiscountInTermination.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.healthPlanDiscountInTermination?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={healthPlanOtherRules === "1" ? 6 : 12}>
                    <Controller
                      name="healthPlanOtherRules"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.healthPlanOtherRules}>
                          <InputLabel id="health-plan-other-rules-label">Outras regras?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="health-plan-other-rules-label"
                            label="Outras regras?"
                          >
                            {benefitsOptions.healthPlanOtherRules.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.healthPlanOtherRules?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {healthPlanOtherRules === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="healthPlanOtherRulesDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.healthPlanOtherRulesDescription}
                            helperText={errors.healthPlanOtherRulesDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Plano Odontológico */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Plano Odontológico</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasDentalPlan"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasDentalPlan}>
                  <InputLabel id="has-dental-plan-label">Plano Odontológico</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-dental-plan-label"
                    label="Plano Odontológico"
                  >
                    {benefitsOptions.hasDentalPlan.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasDentalPlan?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasDentalPlan === "1" && (
            <>
              {dentalPlanFields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <DentalPlanOperatorFieldset
                    index={index}
                    disabled={benefitsIsValidated}
                    onRemove={() => removeDentalPlan(index)}
                    canRemove={dentalPlanFields.length > 1}
                  />
                </Grid>
              ))}
              {!benefitsIsValidated && (
                <Grid item xs={12}>
                  <Button variant="outlined" size="small" onClick={() => appendDentalPlan(emptyDentalPlanOperator)}>
                    Adicionar outra operadora
                  </Button>
                </Grid>
              )}
            </>
          )}

          {hasDentalPlan === "1" && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={dentalPlanDiscountOnPayroll === "5" ? 6 : 12}>
                    <Controller
                      name="dentalPlanDiscountOnPayroll"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.dentalPlanDiscountOnPayroll}>
                          <InputLabel id="dental-plan-discount-on-payroll-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="dental-plan-discount-on-payroll-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.dentalPlanDiscountOnPayroll.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.dentalPlanDiscountOnPayroll?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {dentalPlanDiscountOnPayroll === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="dentalPlanDiscountOnPayrollOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.dentalPlanDiscountOnPayrollOther}
                            helperText={errors.dentalPlanDiscountOnPayrollOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="dentalPlanDiscountInTermination"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.dentalPlanDiscountInTermination}>
                      <InputLabel id="dental-plan-discount-in-termination-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="dental-plan-discount-in-termination-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.dentalPlanDiscountInTermination.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.dentalPlanDiscountInTermination?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={dentalPlanOtherRules === "1" ? 6 : 12}>
                    <Controller
                      name="dentalPlanOtherRules"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.dentalPlanOtherRules}>
                          <InputLabel id="dental-plan-other-rules-label">Outras regras?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="dental-plan-other-rules-label"
                            label="Outras regras?"
                          >
                            {benefitsOptions.dentalPlanOtherRules.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.dentalPlanOtherRules?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {dentalPlanOtherRules === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="dentalPlanOtherRulesDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.dentalPlanOtherRulesDescription}
                            helperText={errors.dentalPlanOtherRulesDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Vale-Transporte */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Vale-Transporte</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasTransportationVoucher"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasTransportationVoucher}>
                  <InputLabel id="has-transportation-voucher-label">Vale-Transporte</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-transportation-voucher-label"
                    label="Vale-Transporte"
                  >
                    {benefitsOptions.hasTransportationVoucher.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasTransportationVoucher?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasTransportationVoucher === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="transportationOperatorName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Operadora (Nome)"
                      size="small"
                      fullWidth
                      error={!!errors.transportationOperatorName}
                      helperText={errors.transportationOperatorName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.transportationCalculationPeriod}
                      helperText={errors.transportationCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationDeductionPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de desconto (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.transportationDeductionPeriod}
                      helperText={errors.transportationDeductionPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationCalcExecution"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationCalcExecution}>
                      <InputLabel id="transportation-calc-execution-label">Cálculo executado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-calc-execution-label"
                        label="Cálculo executado"
                      >
                        {benefitsOptions.transportationCalcExecution.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationCalcExecution?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationPurchaseDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Prazo de compra (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.transportationPurchaseDeadline}
                      helperText={errors.transportationPurchaseDeadline?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationDeductAbsences"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationDeductAbsences}>
                      <InputLabel id="transportation-deduct-absences-label">Faltas são deduzidas do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-deduct-absences-label"
                        label="Faltas são deduzidas do cálculo"
                      >
                        {benefitsOptions.transportationDeductAbsences.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationDeductAbsences?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationDeductMedicalCertificates"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationDeductMedicalCertificates}>
                      <InputLabel id="transportation-deduct-medical-certificates-label">Atestados são deduzidos do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-deduct-medical-certificates-label"
                        label="Atestados são deduzidos do cálculo"
                      >
                        {benefitsOptions.transportationDeductMedicalCertificates.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationDeductMedicalCertificates?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(transportationBenefitForAccidentLeave) ? 6 : 12}>
                    <Controller
                      name="transportationBenefitForAccidentLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.transportationBenefitForAccidentLeave}>
                          <InputLabel id="transportation-benefit-for-accident-leave-label">Afastados por Acidente Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="transportation-benefit-for-accident-leave-label"
                            label="Afastados por Acidente Recebem o Benefício"
                          >
                            {benefitsOptions.transportationBenefitForAccidentLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.transportationBenefitForAccidentLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {transportationBenefitForAccidentLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationAccidentLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.transportationAccidentLeaveLimit}
                            helperText={errors.transportationAccidentLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {transportationBenefitForAccidentLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationBenefitForAccidentLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.transportationBenefitForAccidentLeaveOther}
                            helperText={errors.transportationBenefitForAccidentLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(transportationBenefitForIllnessLeave) ? 6 : 12}>
                    <Controller
                      name="transportationBenefitForIllnessLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.transportationBenefitForIllnessLeave}>
                          <InputLabel id="transportation-benefit-for-illness-leave-label">Afastados por Doença Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="transportation-benefit-for-illness-leave-label"
                            label="Afastados por Doença Recebem o Benefício"
                          >
                            {benefitsOptions.transportationBenefitForIllnessLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.transportationBenefitForIllnessLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {transportationBenefitForIllnessLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationBenefitForIllnessLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.transportationBenefitForIllnessLeaveLimit}
                            helperText={errors.transportationBenefitForIllnessLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {transportationBenefitForIllnessLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationBenefitForIllnessLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.transportationBenefitForIllnessLeaveOther}
                            helperText={errors.transportationBenefitForIllnessLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(transportationBenefitForMaternityLeave) ? 6 : 12}>
                    <Controller
                      name="transportationBenefitForMaternityLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.transportationBenefitForMaternityLeave}>
                          <InputLabel id="transportation-benefit-for-maternity-leave-label">Licença Maternidade Recebe o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="transportation-benefit-for-maternity-leave-label"
                            label="Licença Maternidade Recebe o Benefício"
                          >
                            {benefitsOptions.transportationBenefitForMaternityLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.transportationBenefitForMaternityLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {transportationBenefitForMaternityLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationBenefitForMaternityLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.transportationBenefitForMaternityLeaveLimit}
                            helperText={errors.transportationBenefitForMaternityLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {transportationBenefitForMaternityLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationBenefitForMaternityLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.transportationBenefitForMaternityLeaveOther}
                            helperText={errors.transportationBenefitForMaternityLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationBenefitDuringVacation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationBenefitDuringVacation}>
                      <InputLabel id="transportation-benefit-during-vacation-label">Férias Recebe o Benefício</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-benefit-during-vacation-label"
                        label="Férias Recebe o Benefício"
                      >
                        {benefitsOptions.transportationBenefitDuringVacation.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationBenefitDuringVacation?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={transportationOtherDeductionFactors === "1" ? 6 : 12}>
                    <Controller
                      name="transportationOtherDeductionFactors"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.transportationOtherDeductionFactors}>
                          <InputLabel id="transportation-other-deduction-factors-label">Outros fatores de dedução?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="transportation-other-deduction-factors-label"
                            label="Outros fatores de dedução?"
                          >
                            {benefitsOptions.transportationOtherDeductionFactors.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.transportationOtherDeductionFactors?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {transportationOtherDeductionFactors === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationDeductionRuleDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.transportationDeductionRuleDescription}
                            helperText={errors.transportationDeductionRuleDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={transportationPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="transportationPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.transportationPayrollDeduction}>
                          <InputLabel id="transportation-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="transportation-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.transportationPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.transportationPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {transportationPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="transportationPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.transportationPayrollDeductionOther}
                            helperText={errors.transportationPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationTerminationDeduction}>
                      <InputLabel id="transportation-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.transportationTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="transportationUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.transportationUnusedBenefitDeduction}>
                      <InputLabel id="transportation-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="transportation-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.transportationUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.transportationUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Vale-Mobilidade */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Vale-Mobilidade</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasMobilityVoucher"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasMobilityVoucher}>
                  <InputLabel id="has-mobility-voucher-label">Vale-Mobilidade</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-mobility-voucher-label"
                    label="Vale-Mobilidade"
                  >
                    {benefitsOptions.hasMobilityVoucher.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasMobilityVoucher?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasMobilityVoucher === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="mobilityOperatorName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Operadora (Nome)"
                      size="small"
                      fullWidth
                      error={!!errors.mobilityOperatorName}
                      helperText={errors.mobilityOperatorName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mobilityCalculationPeriod}
                      helperText={errors.mobilityCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityDeductionPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de desconto (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mobilityDeductionPeriod}
                      helperText={errors.mobilityDeductionPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityCalcExecution"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityCalcExecution}>
                      <InputLabel id="mobility-calc-execution-label">Cálculo executado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-calc-execution-label"
                        label="Cálculo executado"
                      >
                        {benefitsOptions.mobilityCalcExecution.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityCalcExecution?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityPurchaseDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Prazo de compra (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mobilityPurchaseDeadline}
                      helperText={errors.mobilityPurchaseDeadline?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityDeductAbsences"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityDeductAbsences}>
                      <InputLabel id="mobility-deduct-absences-label">Faltas são deduzidas do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-deduct-absences-label"
                        label="Faltas são deduzidas do cálculo"
                      >
                        {benefitsOptions.mobilityDeductAbsences.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityDeductAbsences?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityDeductMedicalCertificates"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityDeductMedicalCertificates}>
                      <InputLabel id="mobility-deduct-medical-certificates-label">Atestados são deduzidos do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-deduct-medical-certificates-label"
                        label="Atestados são deduzidos do cálculo"
                      >
                        {benefitsOptions.mobilityDeductMedicalCertificates.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityDeductMedicalCertificates?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mobilityBenefitForAccidentLeave) ? 6 : 12}>
                    <Controller
                      name="mobilityBenefitForAccidentLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mobilityBenefitForAccidentLeave}>
                          <InputLabel id="mobility-benefit-for-accident-leave-label">Afastados por Acidente Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="mobility-benefit-for-accident-leave-label"
                            label="Afastados por Acidente Recebem o Benefício"
                          >
                            {benefitsOptions.mobilityBenefitForAccidentLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mobilityBenefitForAccidentLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mobilityBenefitForAccidentLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityAccidentLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mobilityAccidentLeaveLimit}
                            helperText={errors.mobilityAccidentLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mobilityBenefitForAccidentLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityBenefitForAccidentLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mobilityBenefitForAccidentLeaveOther}
                            helperText={errors.mobilityBenefitForAccidentLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mobilityBenefitForIllnessLeave) ? 6 : 12}>
                    <Controller
                      name="mobilityBenefitForIllnessLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mobilityBenefitForIllnessLeave}>
                          <InputLabel id="mobility-benefit-for-illness-leave-label">Afastados por Doença Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="mobility-benefit-for-illness-leave-label"
                            label="Afastados por Doença Recebem o Benefício"
                          >
                            {benefitsOptions.mobilityBenefitForIllnessLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mobilityBenefitForIllnessLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mobilityBenefitForIllnessLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityBenefitForIllnessLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mobilityBenefitForIllnessLeaveLimit}
                            helperText={errors.mobilityBenefitForIllnessLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mobilityBenefitForIllnessLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityBenefitForIllnessLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mobilityBenefitForIllnessLeaveOther}
                            helperText={errors.mobilityBenefitForIllnessLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mobilityBenefitForMaternityLeave) ? 6 : 12}>
                    <Controller
                      name="mobilityBenefitForMaternityLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mobilityBenefitForMaternityLeave}>
                          <InputLabel id="mobility-benefit-for-maternity-leave-label">Licença Maternidade Recebe o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="mobility-benefit-for-maternity-leave-label"
                            label="Licença Maternidade Recebe o Benefício"
                          >
                            {benefitsOptions.mobilityBenefitForMaternityLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mobilityBenefitForMaternityLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mobilityBenefitForMaternityLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityBenefitForMaternityLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mobilityBenefitForMaternityLeaveLimit}
                            helperText={errors.mobilityBenefitForMaternityLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mobilityBenefitForMaternityLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityBenefitForMaternityLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mobilityBenefitForMaternityLeaveOther}
                            helperText={errors.mobilityBenefitForMaternityLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityBenefitDuringVacation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityBenefitDuringVacation}>
                      <InputLabel id="mobility-benefit-during-vacation-label">Férias Recebe o Benefício</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-benefit-during-vacation-label"
                        label="Férias Recebe o Benefício"
                      >
                        {benefitsOptions.mobilityBenefitDuringVacation.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityBenefitDuringVacation?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={mobilityOtherDeductionFactors === "1" ? 6 : 12}>
                    <Controller
                      name="mobilityOtherDeductionFactors"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mobilityOtherDeductionFactors}>
                          <InputLabel id="mobility-other-deduction-factors-label">Outros fatores de dedução?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="mobility-other-deduction-factors-label"
                            label="Outros fatores de dedução?"
                          >
                            {benefitsOptions.mobilityOtherDeductionFactors.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mobilityOtherDeductionFactors?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mobilityOtherDeductionFactors === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityDeductionRuleDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.mobilityDeductionRuleDescription}
                            helperText={errors.mobilityDeductionRuleDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={mobilityPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="mobilityPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mobilityPayrollDeduction}>
                          <InputLabel id="mobility-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="mobility-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.mobilityPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mobilityPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mobilityPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mobilityPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.mobilityPayrollDeductionOther}
                            helperText={errors.mobilityPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityTerminationDeduction}>
                      <InputLabel id="mobility-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.mobilityTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mobilityUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mobilityUnusedBenefitDeduction}>
                      <InputLabel id="mobility-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="mobility-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.mobilityUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mobilityUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Vale-Alimentação */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Vale-Alimentação</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasFoodVoucher"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasFoodVoucher}>
                  <InputLabel id="has-food-voucher-label">Vale-Alimentação</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-food-voucher-label"
                    label="Vale-Alimentação"
                  >
                    {benefitsOptions.hasFoodVoucher.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasFoodVoucher?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasFoodVoucher === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="foodOperatorName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Operadora (Nome)"
                      size="small"
                      fullWidth
                      error={!!errors.foodOperatorName}
                      helperText={errors.foodOperatorName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.foodCalculationPeriod}
                      helperText={errors.foodCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodDeductionPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de desconto (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.foodDeductionPeriod}
                      helperText={errors.foodDeductionPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodCalcExecution"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodCalcExecution}>
                      <InputLabel id="food-calc-execution-label">Cálculo executado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-calc-execution-label"
                        label="Cálculo executado"
                      >
                        {benefitsOptions.foodCalcExecution.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodCalcExecution?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodPurchaseDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Prazo de compra (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.foodPurchaseDeadline}
                      helperText={errors.foodPurchaseDeadline?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodDeductAbsences"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodDeductAbsences}>
                      <InputLabel id="food-deduct-absences-label">Faltas são deduzidas do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-deduct-absences-label"
                        label="Faltas são deduzidas do cálculo"
                      >
                        {benefitsOptions.foodDeductAbsences.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodDeductAbsences?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodDeductMedicalCertificates"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodDeductMedicalCertificates}>
                      <InputLabel id="food-deduct-medical-certificates-label">Atestados são deduzidos do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-deduct-medical-certificates-label"
                        label="Atestados são deduzidos do cálculo"
                      >
                        {benefitsOptions.foodDeductMedicalCertificates.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodDeductMedicalCertificates?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(foodBenefitForAccidentLeave) ? 6 : 12}>
                    <Controller
                      name="foodBenefitForAccidentLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.foodBenefitForAccidentLeave}>
                          <InputLabel id="food-benefit-for-accident-leave-label">Afastados por Acidente Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="food-benefit-for-accident-leave-label"
                            label="Afastados por Acidente Recebem o Benefício"
                          >
                            {benefitsOptions.foodBenefitForAccidentLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.foodBenefitForAccidentLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {foodBenefitForAccidentLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodAccidentLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.foodAccidentLeaveLimit}
                            helperText={errors.foodAccidentLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {foodBenefitForAccidentLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodBenefitForAccidentLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.foodBenefitForAccidentLeaveOther}
                            helperText={errors.foodBenefitForAccidentLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(foodBenefitForIllnessLeave) ? 6 : 12}>
                    <Controller
                      name="foodBenefitForIllnessLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.foodBenefitForIllnessLeave}>
                          <InputLabel id="food-benefit-for-illness-leave-label">Afastados por Doença Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="food-benefit-for-illness-leave-label"
                            label="Afastados por Doença Recebem o Benefício"
                          >
                            {benefitsOptions.foodBenefitForIllnessLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.foodBenefitForIllnessLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {foodBenefitForIllnessLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodBenefitForIllnessLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.foodBenefitForIllnessLeaveLimit}
                            helperText={errors.foodBenefitForIllnessLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {foodBenefitForIllnessLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodBenefitForIllnessLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.foodBenefitForIllnessLeaveOther}
                            helperText={errors.foodBenefitForIllnessLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(foodBenefitForMaternityLeave) ? 6 : 12}>
                    <Controller
                      name="foodBenefitForMaternityLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.foodBenefitForMaternityLeave}>
                          <InputLabel id="food-benefit-for-maternity-leave-label">Licença Maternidade Recebe o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="food-benefit-for-maternity-leave-label"
                            label="Licença Maternidade Recebe o Benefício"
                          >
                            {benefitsOptions.foodBenefitForMaternityLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.foodBenefitForMaternityLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {foodBenefitForMaternityLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodBenefitForMaternityLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.foodBenefitForMaternityLeaveLimit}
                            helperText={errors.foodBenefitForMaternityLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {foodBenefitForMaternityLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodBenefitForMaternityLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.foodBenefitForMaternityLeaveOther}
                            helperText={errors.foodBenefitForMaternityLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="foodBenefitDuringVacation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodBenefitDuringVacation}>
                      <InputLabel id="food-benefit-during-vacation-label">Férias Recebe o Benefício</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-benefit-during-vacation-label"
                        label="Férias Recebe o Benefício"
                      >
                        {benefitsOptions.foodBenefitDuringVacation.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodBenefitDuringVacation?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={foodOtherDeductionFactors === "1" ? 6 : 12}>
                    <Controller
                      name="foodOtherDeductionFactors"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.foodOtherDeductionFactors}>
                          <InputLabel id="food-other-deduction-factors-label">Outros fatores de dedução?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="food-other-deduction-factors-label"
                            label="Outros fatores de dedução?"
                          >
                            {benefitsOptions.foodOtherDeductionFactors.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.foodOtherDeductionFactors?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {foodOtherDeductionFactors === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodDeductionRuleDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.foodDeductionRuleDescription}
                            helperText={errors.foodDeductionRuleDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={foodPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="foodPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.foodPayrollDeduction}>
                          <InputLabel id="food-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="food-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.foodPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.foodPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {foodPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="foodPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.foodPayrollDeductionOther}
                            helperText={errors.foodPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="foodTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodTerminationDeduction}>
                      <InputLabel id="food-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.foodTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="foodUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.foodUnusedBenefitDeduction}>
                      <InputLabel id="food-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="food-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.foodUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.foodUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Vale-Refeição */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Vale-Refeição</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasMealVoucher"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasMealVoucher}>
                  <InputLabel id="has-meal-voucher-label">Vale-Refeição</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-meal-voucher-label"
                    label="Vale-Refeição"
                  >
                    {benefitsOptions.hasMealVoucher.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasMealVoucher?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasMealVoucher === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="mealOperatorName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Operadora (Nome)"
                      size="small"
                      fullWidth
                      error={!!errors.mealOperatorName}
                      helperText={errors.mealOperatorName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mealCalculationPeriod}
                      helperText={errors.mealCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealDeductionPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de desconto (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mealDeductionPeriod}
                      helperText={errors.mealDeductionPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealCalcExecution"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealCalcExecution}>
                      <InputLabel id="meal-calc-execution-label">Cálculo executado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-calc-execution-label"
                        label="Cálculo executado"
                      >
                        {benefitsOptions.mealCalcExecution.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealCalcExecution?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealPurchaseDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Prazo de compra (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.mealPurchaseDeadline}
                      helperText={errors.mealPurchaseDeadline?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealDeductAbsences"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealDeductAbsences}>
                      <InputLabel id="meal-deduct-absences-label">Faltas são deduzidas do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-deduct-absences-label"
                        label="Faltas são deduzidas do cálculo"
                      >
                        {benefitsOptions.mealDeductAbsences.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealDeductAbsences?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealDeductMedicalCertificates"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealDeductMedicalCertificates}>
                      <InputLabel id="meal-deduct-medical-certificates-label">Atestados são deduzidos do cálculo</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-deduct-medical-certificates-label"
                        label="Atestados são deduzidos do cálculo"
                      >
                        {benefitsOptions.mealDeductMedicalCertificates.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealDeductMedicalCertificates?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mealBenefitForAccidentLeave) ? 6 : 12}>
                    <Controller
                      name="mealBenefitForAccidentLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mealBenefitForAccidentLeave}>
                          <InputLabel id="meal-benefit-for-accident-leave-label">Afastados por Acidente Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="meal-benefit-for-accident-leave-label"
                            label="Afastados por Acidente Recebem o Benefício"
                          >
                            {benefitsOptions.mealBenefitForAccidentLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mealBenefitForAccidentLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mealBenefitForAccidentLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealAccidentLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mealAccidentLeaveLimit}
                            helperText={errors.mealAccidentLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mealBenefitForAccidentLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealBenefitForAccidentLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mealBenefitForAccidentLeaveOther}
                            helperText={errors.mealBenefitForAccidentLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mealBenefitForIllnessLeave) ? 6 : 12}>
                    <Controller
                      name="mealBenefitForIllnessLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mealBenefitForIllnessLeave}>
                          <InputLabel id="meal-benefit-for-illness-leave-label">Afastados por Doença Recebem o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="meal-benefit-for-illness-leave-label"
                            label="Afastados por Doença Recebem o Benefício"
                          >
                            {benefitsOptions.mealBenefitForIllnessLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mealBenefitForIllnessLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mealBenefitForIllnessLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealBenefitForIllnessLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mealBenefitForIllnessLeaveLimit}
                            helperText={errors.mealBenefitForIllnessLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mealBenefitForIllnessLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealBenefitForIllnessLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mealBenefitForIllnessLeaveOther}
                            helperText={errors.mealBenefitForIllnessLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={["1", "3"].includes(mealBenefitForMaternityLeave) ? 6 : 12}>
                    <Controller
                      name="mealBenefitForMaternityLeave"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mealBenefitForMaternityLeave}>
                          <InputLabel id="meal-benefit-for-maternity-leave-label">Licença Maternidade Recebe o Benefício</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="meal-benefit-for-maternity-leave-label"
                            label="Licença Maternidade Recebe o Benefício"
                          >
                            {benefitsOptions.mealBenefitForMaternityLeave.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mealBenefitForMaternityLeave?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mealBenefitForMaternityLeave === "3" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealBenefitForMaternityLeaveLimit"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Prazo Limite (dia do mês)"
                            size="small"
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, max: 31 }}
                            onChange={e => {
                              const val = e.target.value.replace(/\D/g, "");
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 31)) {
                                field.onChange(val);
                              }
                            }}
                            error={!!errors.mealBenefitForMaternityLeaveLimit}
                            helperText={errors.mealBenefitForMaternityLeaveLimit?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {mealBenefitForMaternityLeave === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealBenefitForMaternityLeaveOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Informar"
                            size="small"
                            fullWidth
                            error={!!errors.mealBenefitForMaternityLeaveOther}
                            helperText={errors.mealBenefitForMaternityLeaveOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="mealBenefitDuringVacation"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealBenefitDuringVacation}>
                      <InputLabel id="meal-benefit-during-vacation-label">Férias Recebe o Benefício</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-benefit-during-vacation-label"
                        label="Férias Recebe o Benefício"
                      >
                        {benefitsOptions.mealBenefitDuringVacation.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealBenefitDuringVacation?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={mealOtherDeductionFactors === "1" ? 6 : 12}>
                    <Controller
                      name="mealOtherDeductionFactors"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mealOtherDeductionFactors}>
                          <InputLabel id="meal-other-deduction-factors-label">Outros fatores de dedução?</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="meal-other-deduction-factors-label"
                            label="Outros fatores de dedução?"
                          >
                            {benefitsOptions.mealOtherDeductionFactors.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mealOtherDeductionFactors?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mealOtherDeductionFactors === "1" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealDeductionRuleDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.mealDeductionRuleDescription}
                            helperText={errors.mealDeductionRuleDescription?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={mealPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="mealPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.mealPayrollDeduction}>
                          <InputLabel id="meal-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="meal-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.mealPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.mealPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {mealPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="mealPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.mealPayrollDeductionOther}
                            helperText={errors.mealPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="mealTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealTerminationDeduction}>
                      <InputLabel id="meal-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.mealTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="mealUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.mealUnusedBenefitDeduction}>
                      <InputLabel id="meal-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="meal-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.mealUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.mealUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Cesta Básica */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Cesta Básica</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasBasicBasket"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasBasicBasket}>
                  <InputLabel id="has-basic-basket-label">Cesta Básica</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-basic-basket-label"
                    label="Cesta Básica"
                  >
                    {benefitsOptions.hasBasicBasket.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasBasicBasket?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasBasicBasket === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="basicBasketProvider"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Fornecedor"
                      size="small"
                      fullWidth
                      error={!!errors.basicBasketProvider}
                      helperText={errors.basicBasketProvider?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="basicBasketCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.basicBasketCalculationPeriod}
                      helperText={errors.basicBasketCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="basicBasketBusinessRule"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!errors.basicBasketBusinessRule}
                      helperText={errors.basicBasketBusinessRule?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={basicBasketPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="basicBasketPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.basicBasketPayrollDeduction}>
                          <InputLabel id="basic-basket-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="basic-basket-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.basicBasketPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.basicBasketPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {basicBasketPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="basicBasketPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.basicBasketPayrollDeductionOther}
                            helperText={errors.basicBasketPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="basicBasketTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.basicBasketTerminationDeduction}>
                      <InputLabel id="basic-basket-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="basic-basket-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.basicBasketTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.basicBasketTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="basicBasketUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.basicBasketUnusedBenefitDeduction}>
                      <InputLabel id="basic-basket-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="basic-basket-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.basicBasketUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.basicBasketUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Refeitório */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Refeitório</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasCanteen"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasCanteen}>
                  <InputLabel id="has-canteen-label">Refeitório</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-canteen-label"
                    label="Refeitório"
                  >
                    {benefitsOptions.hasCanteen.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasCanteen?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasCanteen === "1" && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="canteenCalculationPeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Período de cálculo (ex: 05/20)"
                      size="small"
                      fullWidth
                      InputProps={{ inputComponent: ClosePeriodMask }}
                      error={!!errors.canteenCalculationPeriod}
                      helperText={errors.canteenCalculationPeriod?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="canteenBusinessRule"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Regra de Negócio"
                      size="small"
                      fullWidth
                      error={!!errors.canteenBusinessRule}
                      helperText={errors.canteenBusinessRule?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={canteenPayrollDeduction === "5" ? 6 : 12}>
                    <Controller
                      name="canteenPayrollDeduction"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" required error={!!errors.canteenPayrollDeduction}>
                          <InputLabel id="canteen-payroll-deduction-label">Desconto em folha</InputLabel>
                          <Select
                            {...field}
                            required disabled={benefitsIsValidated}
                            labelId="canteen-payroll-deduction-label"
                            label="Desconto em folha"
                          >
                            {benefitsOptions.canteenPayrollDeduction.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{errors.canteenPayrollDeduction?.message}</FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {canteenPayrollDeduction === "5" && (
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="canteenPayrollDeductionOther"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required disabled={benefitsIsValidated}
                            label="Descreva a Regra de Negócio"
                            size="small"
                            fullWidth
                            error={!!errors.canteenPayrollDeductionOther}
                            helperText={errors.canteenPayrollDeductionOther?.message}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="canteenTerminationDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.canteenTerminationDeduction}>
                      <InputLabel id="canteen-termination-deduction-label">Desconto na rescisão</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="canteen-termination-deduction-label"
                        label="Desconto na rescisão"
                      >
                        {benefitsOptions.canteenTerminationDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.canteenTerminationDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="canteenUnusedBenefitDeduction"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small" required error={!!errors.canteenUnusedBenefitDeduction}>
                      <InputLabel id="canteen-unused-benefit-deduction-label">Desconto benefício não utilizado</InputLabel>
                      <Select
                        {...field}
                        required disabled={benefitsIsValidated}
                        labelId="canteen-unused-benefit-deduction-label"
                        label="Desconto benefício não utilizado"
                      >
                        {benefitsOptions.canteenUnusedBenefitDeduction.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.canteenUnusedBenefitDeduction?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Outros Benefícios */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Outros Benefícios</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasOtherBenefits"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasOtherBenefits}>
                  <InputLabel id="has-other-benefits-label">Outros Benefícios</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-other-benefits-label"
                    label="Outros Benefícios"
                  >
                    {benefitsOptions.hasOtherBenefits.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasOtherBenefits?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasOtherBenefits === "1" && (
            <>
              {otherBenefitsFields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary" }}>
                        Benefício {index + 1}
                      </Typography>
                      {otherBenefitsFields.length > 1 && !benefitsIsValidated && (
                        <IconButton onClick={() => removeOtherBenefit(index)} color="error" size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name={`otherBenefits.${index}.description`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              required disabled={benefitsIsValidated}
                              label="Descrição"
                              size="small"
                              fullWidth
                              error={!!errors.otherBenefits?.[index]?.description}
                              helperText={errors.otherBenefits?.[index]?.description?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name={`otherBenefits.${index}.businessRule`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              required disabled={benefitsIsValidated}
                              label="Regra de Negócio"
                              size="small"
                              fullWidth
                              error={!!errors.otherBenefits?.[index]?.businessRule}
                              helperText={errors.otherBenefits?.[index]?.businessRule?.message}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
              {!benefitsIsValidated && (
                <Grid item xs={12}>
                  <Button variant="outlined" size="small" onClick={() => appendOtherBenefit(emptyOtherBenefit)}>
                    Adicionar outro benefício
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Paper>

      {/* Convênio */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Convênio</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasPharmacy"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasPharmacy}>
                  <InputLabel id="has-pharmacy-label">Convênio</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-pharmacy-label"
                    label="Convênio"
                  >
                    {benefitsOptions.hasPharmacy.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasPharmacy?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {hasPharmacy === "1" && (
            <>
              {agreementsFields.map((field, index) => (
                <Grid item xs={12} key={field.id}>
                  <AgreementFieldset
                    index={index}
                    disabled={benefitsIsValidated}
                    onRemove={() => removeAgreement(index)}
                    canRemove={agreementsFields.length > 1}
                  />
                </Grid>
              ))}
              {!benefitsIsValidated && (
                <Grid item xs={12}>
                  <Button variant="outlined" size="small" onClick={() => appendAgreement(emptyAgreement)}>
                    Adicionar outro convênio
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Paper>

      {/* Acessos | Procuração */}
      <Paper elevation={2} sx={paperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Acessos | Procuração</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Controller
              name="hasSocialSecurity"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasSocialSecurity}>
                  <InputLabel id="has-social-security-label">Possui Previdência Social?</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-social-security-label"
                    label="Possui Previdência Social?"
                  >
                    {benefitsOptions.hasSocialSecurity.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasSocialSecurity?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {hasSocialSecurity === "1" && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="socialSecurityUser"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Usuário"
                      size="small"
                      fullWidth
                      error={!!errors.socialSecurityUser}
                      helperText={errors.socialSecurityUser?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="socialSecurityPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Senha"
                      size="small"
                      fullWidth
                      error={!!errors.socialSecurityPassword}
                      helperText={errors.socialSecurityPassword?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Controller
              name="hasPowerOfAttorney"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" required error={!!errors.hasPowerOfAttorney}>
                  <InputLabel id="has-power-of-attorney-label">Possui Procurações?</InputLabel>
                  <Select
                    {...field}
                    required disabled={benefitsIsValidated}
                    labelId="has-power-of-attorney-label"
                    label="Possui Procurações?"
                  >
                    {benefitsOptions.hasPowerOfAttorney.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.hasPowerOfAttorney?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {hasPowerOfAttorney === "1" && (
            <>
              <Grid item xs={12}>
                <Controller
                  name="eSocialPowerOfAttorneyExpirationDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Data Vencimento Procuração - eSocial"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!errors.eSocialPowerOfAttorneyExpirationDate}
                      helperText={errors.eSocialPowerOfAttorneyExpirationDate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="connectivityPowerOfAttorneyExpirationDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Data Vencimento Procuração - Conectividade"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!errors.connectivityPowerOfAttorneyExpirationDate}
                      helperText={errors.connectivityPowerOfAttorneyExpirationDate?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="employerWebPowerOfAttorneyExpirationDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required disabled={benefitsIsValidated}
                      label="Data Vencimento Procuração - Empregador Web"
                      type="date"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!errors.employerWebPowerOfAttorneyExpirationDate}
                      helperText={errors.employerWebPowerOfAttorneyExpirationDate?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}

        </Grid>
      </Paper>
    </>
  );
}
